import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  useTheme,
  alpha,
  styled,
  Fade,
} from "@mui/material";
import ApiIcon from "@mui/icons-material/Api"; // Ícone: APIs
import SpeedIcon from "@mui/icons-material/Speed"; // Ícone: Performance
import SecurityIcon from "@mui/icons-material/Security"; // Ícone: Segurança
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions"; // Ícone: Integração
import CloudQueueIcon from "@mui/icons-material/CloudQueue"; // Ícone: Escalabilidade (Cloud)
import SupportIcon from "@mui/icons-material/Support"; // Ícone: Suporte Técnico

// Estilização do Card com borda neon sutil
const FeatureCard = styled(Card)(({ theme }) => ({
  height: "100%",
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: "blur(5px)",
  border: `1px solid ${alpha(theme.palette.primary.light, 0.2)}`,
  borderRadius: theme.shape.borderRadius * 1.5,
  transition:
    "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
  boxShadow: `0 0 15px ${alpha(theme.palette.primary.main, 0.1)}`,
  "&:hover": {
    transform: "translateY(-8px)",
    borderColor: theme.palette.primary.light,
    boxShadow: `0 0 30px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
}));

// Estilização do Ícone
const FeatureIconWrapper = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.light,
}));

const features = [
  {
    icon: <ApiIcon sx={{ fontSize: 40 }} />,
    title: "API First Design",
    description:
      "Acesse e gerencie seus planos e dados de forma programática com nossa API robusta e bem documentada.",
    delay: 200,
  },
  {
    icon: <IntegrationInstructionsIcon sx={{ fontSize: 40 }} />,
    title: "Integração Simplificada",
    description:
      "Webhooks e SDKs disponíveis para facilitar a integração com seus sistemas e fluxos de trabalho existentes.",
    delay: 400,
  },
  {
    icon: <SpeedIcon sx={{ fontSize: 40 }} />,
    title: "Alta Performance",
    description:
      "Infraestrutura otimizada para garantir respostas rápidas e disponibilidade constante dos serviços.",
    delay: 600,
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    title: "Segurança Robusta",
    description:
      "Seus dados protegidos com as melhores práticas de segurança, criptografia e conformidade.",
    delay: 800,
  },
  {
    icon: <CloudQueueIcon sx={{ fontSize: 40 }} />,
    title: "Escalabilidade Cloud",
    description:
      "Plataforma construída na nuvem, pronta para escalar de acordo com as necessidades do seu negócio.",
    delay: 1000,
  },
  {
    icon: <SupportIcon sx={{ fontSize: 40 }} />,
    title: "Suporte Técnico Dev",
    description:
      "Equipe de suporte especializada para auxiliar desenvolvedores com integrações e dúvidas técnicas.",
    delay: 1200,
  },
];

export const FeaturesSection = () => {
  const theme = useTheme();

  return (
    <Box
      id="tecnologia" // ID para navegação
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: alpha(theme.palette.background.default, 0.5),
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Efeito de fundo sutil */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 10% 90%, ${alpha(
            theme.palette.secondary.main,
            0.06
          )} 0%, transparent 50%),
                       radial-gradient(circle at 90% 20%, ${alpha(
                         theme.palette.primary.main,
                         0.08
                       )} 0%, transparent 50%)`,
          opacity: 0.6,
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="overline"
            sx={{
              color: theme.palette.secondary.light,
              fontWeight: 600,
              mb: 1,
              display: "block",
            }}
          >
            Nossa Plataforma
          </Typography>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: "2.2rem", md: "3rem" },
              color: theme.palette.text.primary,
            }}
          >
            Tecnologia para Simplificar
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: "700px",
              mx: "auto",
              lineHeight: 1.7,
            }}
          >
            Desenvolvemos nossa plataforma pensando nas necessidades dos
            profissionais de tecnologia, oferecendo controle, flexibilidade e
            performance.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Fade in={true} timeout={feature.delay}>
                <FeatureCard elevation={0}>
                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <FeatureIconWrapper>{feature.icon}</FeatureIconWrapper>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 1.5,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.6 }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </FeatureCard>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};
